import { useEffect, useState } from "react";
import { useAuth } from "../../../../context/AuthContext";
import { jobService } from "../../../../services/job-service";

const RESUME_KEY = "jobzilla_candidate_resume_builder";

const initialResume = {
    headline: "",
    summary: "",
    keySkills: "",
    employment: "",
    education: "",
    itSkills: "",
    projects: "",
    desiredProfile: "",
    personalDetails: "",
    accomplishments: "",
    preferred_category: "",
    preferred_subcategory: "",
    location: ""
};

function CanMyResumePage() {
    const { user, updateProfile } = useAuth();
    const [resume, setResume] = useState(initialResume);
    const [resumes, setResumes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [file, setFile] = useState(null);
    const [message, setMessage] = useState("");
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        jobService.jobCategories()
            .then((data) => setCategories(data.categories || []))
            .catch(console.error);
    }, []);

    useEffect(() => {
        if (user) {
            setResume({
                headline: user.headline || "",
                summary: user.summary || "",
                keySkills: user.skills || "",
                education: user.education || "",
                preferred_category: user.preferred_category || "",
                preferred_subcategory: user.preferred_subcategory || "",
                location: user.location || "",
                employment: localStorage.getItem(`${RESUME_KEY}_employment`) || "",
                itSkills: localStorage.getItem(`${RESUME_KEY}_itSkills`) || "",
                projects: localStorage.getItem(`${RESUME_KEY}_projects`) || "",
                desiredProfile: localStorage.getItem(`${RESUME_KEY}_desiredProfile`) || "",
                personalDetails: localStorage.getItem(`${RESUME_KEY}_personalDetails`) || "",
                accomplishments: localStorage.getItem(`${RESUME_KEY}_accomplishments`) || ""
            });
        }
        loadFiles();
    }, [user]);

    const loadFiles = () => {
        jobService.resumes()
            .then((data) => setResumes(data.resumes || []))
            .catch((error) => setMessage(error.message));
    };

    const update = (event) => setResume({ ...resume, [event.target.name]: event.target.value });

    const save = async (event) => {
        event.preventDefault();
        setSaving(true);
        setMessage("");
        try {
            await updateProfile({
                headline: resume.headline,
                summary: resume.summary,
                skills: resume.keySkills,
                education: resume.education,
                preferred_category: resume.preferred_category,
                preferred_subcategory: resume.preferred_subcategory,
                location: resume.location
            });
            localStorage.setItem(`${RESUME_KEY}_employment`, resume.employment);
            localStorage.setItem(`${RESUME_KEY}_itSkills`, resume.itSkills);
            localStorage.setItem(`${RESUME_KEY}_projects`, resume.projects);
            localStorage.setItem(`${RESUME_KEY}_desiredProfile`, resume.desiredProfile);
            localStorage.setItem(`${RESUME_KEY}_personalDetails`, resume.personalDetails);
            localStorage.setItem(`${RESUME_KEY}_accomplishments`, resume.accomplishments);
            setMessage("Resume details saved successfully to database.");
        } catch (error) {
            setMessage(error.message);
        } finally {
            setSaving(false);
        }
    };

    const upload = async (event) => {
        event.preventDefault();
        if (!file) {
            setMessage("Choose a resume file first.");
            return;
        }
        setUploading(true);
        setMessage("");
        try {
            await jobService.uploadResume(file);
            setFile(null);
            setMessage("Resume file uploaded.");
            loadFiles();
        } catch (error) {
            setMessage(error.message);
        } finally {
            setUploading(false);
        }
    };

    const remove = async (id) => {
        try {
            await jobService.deleteResume(id);
            setMessage("Resume file deleted.");
            loadFiles();
        } catch (error) {
            setMessage(error.message);
        }
    };

    return (
        <div className="twm-right-section-panel site-bg-gray">
            {message && <div className="alert alert-info">{message}</div>}
            <form onSubmit={save}>
                <div className="panel panel-default mb-3">
                    <div className="panel-heading wt-panel-heading p-a20">
                        <h4 className="panel-tittle m-a0">Job Preferences</h4>
                    </div>
                    <div className="panel-body wt-panel-body p-a20">
                        <div className="row">
                            <div className="col-md-6 form-group">
                                <label className="fw-semibold mb-2">Preferred Job Categories (Select Multiple)</label>
                                <div className="border rounded p-2 bg-white" style={{ maxHeight: "150px", overflowY: "auto" }}>
                                    {categories.filter(c => !c.parent_id).map(c => {
                                        const cId = c.slug || c.id;
                                        const selectedCategoryIds = resume.preferred_category ? resume.preferred_category.split(",") : [];
                                        const isChecked = selectedCategoryIds.includes(cId);
                                        return (
                                            <div key={c.id} className="form-check mb-1 d-flex align-items-center gap-2">
                                                <input
                                                    className="form-check-input m-0"
                                                    type="checkbox"
                                                    id={`cat_chk_${c.id}`}
                                                    checked={isChecked}
                                                    onChange={(e) => {
                                                        let newIds;
                                                        if (e.target.checked) {
                                                            newIds = [...selectedCategoryIds, cId];
                                                        } else {
                                                            newIds = selectedCategoryIds.filter(id => id !== cId);
                                                        }
                                                        setResume({ ...resume, preferred_category: newIds.join(","), preferred_subcategory: "" });
                                                    }}
                                                />
                                                <label className="form-check-label small m-0" htmlFor={`cat_chk_${c.id}`}>
                                                    {c.name}
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="col-md-6 form-group">
                                <label className="fw-semibold mb-2">Preferred Job Subcategories (Select Multiple)</label>
                                <div className="border rounded p-2 bg-white" style={{ maxHeight: "150px", overflowY: "auto" }}>
                                    {categories.filter(c => {
                                        if (!c.parent_id) return false;
                                        const selectedCategoryIds = resume.preferred_category ? resume.preferred_category.split(",") : [];
                                        const parentCategory = categories.find(parent => parent.id === c.parent_id);
                                        return selectedCategoryIds.includes(c.parent_id) || (parentCategory && selectedCategoryIds.includes(parentCategory.slug));
                                    }).map(c => {
                                        const cId = c.slug || c.id;
                                        const selectedSubcategoryIds = resume.preferred_subcategory ? resume.preferred_subcategory.split(",") : [];
                                        const isChecked = selectedSubcategoryIds.includes(cId);
                                        return (
                                            <div key={c.id} className="form-check mb-1 d-flex align-items-center gap-2">
                                                <input
                                                    className="form-check-input m-0"
                                                    type="checkbox"
                                                    id={`sub_chk_${c.id}`}
                                                    checked={isChecked}
                                                    onChange={(e) => {
                                                        let newIds;
                                                        if (e.target.checked) {
                                                            newIds = [...selectedSubcategoryIds, cId];
                                                        } else {
                                                            newIds = selectedSubcategoryIds.filter(id => id !== cId);
                                                        }
                                                        setResume({ ...resume, preferred_subcategory: newIds.join(",") });
                                                    }}
                                                />
                                                <label className="form-check-label small m-0" htmlFor={`sub_chk_${c.id}`}>
                                                    {c.name}
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="col-md-12 form-group m-t10">
                                <label>Preferred Location</label>
                                <input className="form-control" type="text" name="location" value={resume.location} onChange={update} placeholder="Example: New York, Remote, etc." />
                            </div>
                        </div>
                    </div>
                </div>
                <ResumeSection title="Resume Headline" name="headline" value={resume.headline} onChange={update} rows={2} placeholder="Example: React Developer with 3 years of frontend experience" />
                <ResumeSection title="Profile Summary" name="summary" value={resume.summary} onChange={update} />
                <ResumeSection title="Key Skills" name="keySkills" value={resume.keySkills} onChange={update} placeholder="React, JavaScript, API Integration, UI Design" />
                <ResumeSection title="Employment" name="employment" value={resume.employment} onChange={update} />
                <ResumeSection title="Education" name="education" value={resume.education} onChange={update} />
                <ResumeSection title="IT Skills" name="itSkills" value={resume.itSkills} onChange={update} />
                <ResumeSection title="Projects" name="projects" value={resume.projects} onChange={update} />
                <ResumeSection title="Desired Career Profile" name="desiredProfile" value={resume.desiredProfile} onChange={update} />
                <ResumeSection title="Personal Details" name="personalDetails" value={resume.personalDetails} onChange={update} />
                <ResumeSection title="Accomplishments" name="accomplishments" value={resume.accomplishments} onChange={update} />
                <div className="text-left m-b30">
                    <button type="submit" className="site-button" disabled={saving}>{saving ? "Saving..." : "Save Resume Details"}</button>
                </div>
            </form>

            <div className="panel panel-default mb-3">
                <div className="panel-heading wt-panel-heading p-a20">
                    <h4 className="panel-tittle m-a0">Attach Resume</h4>
                </div>
                <div className="panel-body wt-panel-body p-a20">
                    <form onSubmit={upload} className="row m-b20">
                        <div className="col-md-8">
                            <input className="form-control" type="file" accept=".pdf,.doc,.docx" onChange={(event) => setFile(event.target.files?.[0] || null)} />
                        </div>
                        <div className="col-md-4">
                            <button type="submit" className="site-button" disabled={uploading}>{uploading ? "Uploading..." : "Upload Resume"}</button>
                        </div>
                    </form>
                    {resumes.length === 0 ? (
                        <div>No uploaded resume files yet.</div>
                    ) : (
                        <ul className="list-unstyled">
                            {resumes.map((item) => (
                                <li key={item.id} className="d-flex justify-content-between align-items-center m-b10">
                                    <span>{item.file_name} {item.size ? `(${formatBytes(item.size)})` : ""}</span>
                                    <button type="button" className="site-button-link site-text-primary" onClick={() => remove(item.id)}>Delete</button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    )
}

function ResumeSection({ title, name, value, onChange, rows = 4, placeholder = "" }) {
    return (
        <div className="panel panel-default mb-3">
            <div className="panel-heading wt-panel-heading p-a20">
                <h4 className="panel-tittle m-a0">{title}</h4>
            </div>
            <div className="panel-body wt-panel-body p-a20">
                <textarea className="form-control" name={name} rows={rows} value={value} onChange={onChange} placeholder={placeholder || `Add ${title.toLowerCase()}`} />
            </div>
        </div>
    );
}

function formatBytes(value) {
    const bytes = Number(value || 0);
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default CanMyResumePage;
